import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChatCircleText, Trash } from "@phosphor-icons/react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Textarea } from "../components/ui/textarea";
import DictateButton from "../components/DictateButton";

const TYPE_COLORS = {
  "Quarterly check-in call": "bg-[#4A6FA5]/10 text-[#4A6FA5]",
  "Home visit": "bg-primary/10 text-primary",
  "After first shift": "bg-[#27AE60]/10 text-[#1e8449]",
  "Supervision visit": "bg-[#B7791F]/10 text-[#8a5d14]",
  "Family call": "bg-[#4A6FA5]/10 text-[#4A6FA5]",
  "Incident follow-up": "bg-[#C0392B]/10 text-[#C0392B]",
};

export default function NotesPage() {
  const { user } = useAuth();
  const [meta, setMeta] = useState(null);
  const [notes, setNotes] = useState(null);
  const [form, setForm] = useState({ client_id: "", employee_id: "", type: "General", note: "" });
  const [filters, setFilters] = useState({ client_id: "", employee_id: "", note_type: "" });
  const [busy, setBusy] = useState(false);

  const load = () => {
    const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v));
    api.get("/notes", { params }).then((r) => setNotes(r.data)).catch((e) => toast.error(formatApiError(e)));
  };
  useEffect(() => { api.get("/notes/meta").then((r) => setMeta(r.data)).catch((e) => toast.error(formatApiError(e))); }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filters]);

  if (!meta) {
    return <div className="flex justify-center py-24"><div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" /></div>;
  }

  const submit = async () => {
    if (!form.note.trim()) { toast.error("Write the note first"); return; }
    if (!form.client_id && !form.employee_id) { toast.error("Choose a client or an employee"); return; }
    setBusy(true);
    try {
      await api.post("/notes", form);
      toast.success("Note filed");
      setForm({ ...form, note: "" });
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (nid) => {
    try {
      await api.delete(`/notes/${nid}`);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="max-w-3xl" data-testid="notes-page">
      <h1 className="font-heading text-4xl font-semibold tracking-tight text-stone-900">Notes</h1>
      <p className="mt-1 text-sm text-stone-500">
        One place for everything — check-in calls, home visits, after-first-shift follow-ups. Pick who it's about, pick a type, write it down. No classification headaches.
      </p>

      <div className="mt-6 rounded-lg border border-stone-200 bg-white p-6 shadow-sm" data-testid="note-composer">
        <div className="mb-3 rounded-lg border border-[#B7791F]/40 bg-[#B7791F]/10 p-3" data-testid="note-verbal-warning">
          <p className="text-xs font-medium leading-relaxed text-[#7c4a03]">
            Incidents, complaints, concerns, and time-sensitive matters must ALSO be reported verbally and promptly — call the office at <a href="tel:2675470310" className="font-semibold underline">267-547-0310</a>. Do not rely on this form alone.
          </p>
          {(form.type || "").toLowerCase().includes("incident") || (form.type || "").toLowerCase().includes("complaint") ? (
            <p className="mt-1.5 text-xs font-semibold text-[#C0392B]">
              This is an incident or complaint — after calling the office, also file the formal report:{" "}
              <Link to="/forms" className="underline">open the incident / complaint form</Link>.
            </p>
          ) : null}
        </div>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <select value={form.client_id} onChange={(e) => setForm({ ...form, client_id: e.target.value })}
            className="rounded-md border border-stone-300 bg-white px-3 py-2 text-sm" data-testid="note-client-select">
            <option value="">Client (by code)…</option>
            {meta.clients.map((c) => <option key={c.id} value={c.id}>{c.full_name || c.client_code}</option>)}
          </select>
          <select value={form.employee_id} onChange={(e) => setForm({ ...form, employee_id: e.target.value })}
            className="rounded-md border border-stone-300 bg-white px-3 py-2 text-sm" data-testid="note-employee-select">
            <option value="">Employee…</option>
            {meta.employees.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
          </select>
        </div>
        <div className="mt-3 flex flex-wrap gap-1.5">
          {meta.types.map((t) => (
            <button key={t} type="button" onClick={() => setForm({ ...form, type: t })}
              className={`rounded-full border px-3 py-1 text-xs font-medium transition-colors ${form.type === t ? "border-primary bg-primary text-primary-foreground" : "border-stone-300 text-stone-600 hover:bg-accent"}`}
              data-testid={`note-type-${t.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
              {t}
            </button>
          ))}
        </div>
        <div className="mt-3 flex justify-end">
          <DictateButton testid="note-dictate" value={form.note} onReplace={(t) => setForm((f) => ({ ...f, note: t }))} onText={(t) => setForm((f) => ({ ...f, note: (f.note ? f.note + " " : "") + t }))} />
        </div>
        <Textarea rows={4} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })}
          className="mt-1.5 border-stone-300 bg-white text-sm" placeholder="Just write the note — it's filed automatically."
          data-testid="note-text" />
        <div className="mt-3 flex justify-end">
          <Button onClick={submit} disabled={busy} className="bg-primary text-primary-foreground hover:bg-stone-900" data-testid="note-submit">
            {busy ? "Filing..." : "File Note"}
          </Button>
        </div>
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-2" data-testid="notes-filters">
        <select value={filters.client_id} onChange={(e) => setFilters({ ...filters, client_id: e.target.value })}
          className="rounded-md border border-stone-300 bg-white px-3 py-1.5 text-xs" data-testid="filter-client">
          <option value="">All clients</option>
          {meta.clients.map((c) => <option key={c.id} value={c.id}>{c.full_name || c.client_code}</option>)}
        </select>
        <select value={filters.note_type} onChange={(e) => setFilters({ ...filters, note_type: e.target.value })}
          className="rounded-md border border-stone-300 bg-white px-3 py-1.5 text-xs" data-testid="filter-type">
          <option value="">All types</option>
          {meta.types.map((t) => <option key={t} value={t}>{t}</option>)}
        </select>
      </div>

      <div className="mt-4 space-y-3" data-testid="notes-feed">
        {notes === null ? (
          <div className="flex justify-center py-10"><div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" /></div>
        ) : notes.length === 0 ? (
          <p className="rounded-lg border border-stone-200 bg-white p-6 text-center text-sm text-stone-500" data-testid="notes-empty">No notes yet — file the first one above.</p>
        ) : notes.map((n) => (
          <div key={n.id} className="rounded-lg border border-stone-200 bg-white p-4 shadow-sm" data-testid={`note-${n.id}`}>
            <div className="flex flex-wrap items-center gap-2">
              <span className={`rounded-full px-2.5 py-0.5 text-[11px] font-medium ${TYPE_COLORS[n.type] || "bg-accent text-accent-foreground"}`}>{n.type}</span>
              {n.client_code && <span className="text-xs font-semibold text-stone-700" data-testid={`note-client-${n.id}`}>{n.client_name || n.client_code}</span>}
              {n.employee_name && <span className="text-xs font-semibold text-stone-700" data-testid={`note-employee-${n.id}`}>{n.employee_name}</span>}
              <span className="ml-auto text-[11px] text-stone-400">{n.author_name} · {String(n.created_at).slice(0, 16).replace("T", " ")}</span>
              {(user?.role === "admin" && sessionStorage.getItem("shc_view_staff") !== "1" || n.author_email === user?.email) && (
                <button onClick={() => remove(n.id)} className="text-stone-300 hover:text-[#C0392B]" data-testid={`note-delete-${n.id}`}><Trash size={14} /></button>
              )}
            </div>
            <p className="mt-2 whitespace-pre-wrap text-sm leading-relaxed text-stone-700">{n.note}</p>
          </div>
        ))}
      </div>
    </div>
  );
}
